import React, { useEffect, useState, useRef } from 'react';
import { Row, Col, Card, Form, Button, Alert, Modal, Tag, List, Input, Slider, message } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, ArrowLeftOutlined, DeleteOutlined, EditOutlined, ScissorOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchVideoDetails, trimVideo } from '../../../Service/VideoEdit/VideoEdit_API';
import { selectVideoEdit } from '../../../Store/VideoEdit/AdminVideoEditReducer';
import { getVideoUrl } from '../../../Service/apiConfig';
import '../../../Assets/css/VideoEdit.css';

const AdminEditVideo = ({ video_id, onBack }) => {
  const dispatch = useDispatch();
  const videoRef = useRef(null);
  
  const { videoDetails, loading, error, triming } = useSelector(selectVideoEdit);
  
  // State for multiple segments
  const [segments, setSegments] = useState([]);
  const [currentSegmentName, setCurrentSegmentName] = useState('Segment 1');
  const [currentStart, setCurrentStart] = useState(0);
  const [currentEnd, setCurrentEnd] = useState(0);
  
  const [startInputVal, setStartInputVal] = useState('00:00:00');
  const [endInputVal, setEndInputVal] = useState('00:00:00');

  // Helper to format seconds to HH:MM:SS string
  const secondsToTimeStr = (seconds) => {
    if (isNaN(seconds) || seconds < 0) return '00:00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper to parse HH:MM:SS string to seconds number
  const timeToSeconds = (timeStr) => {
    if (!timeStr) return 0;
    const parts = timeStr.split(':').map(Number);
    if (parts.length === 3) {
      const [hours, minutes, seconds] = parts;
      return (hours * 3600) + (minutes * 60) + (seconds || 0);
    } else if (parts.length === 2) {
      const [minutes, seconds] = parts;
      return (minutes * 60) + (seconds || 0);
    } else {
      return parseFloat(timeStr) || 0;
    }
  };

  useEffect(() => {
    setStartInputVal(secondsToTimeStr(currentStart));
  }, [currentStart]);

  useEffect(() => {
    setEndInputVal(secondsToTimeStr(currentEnd));
  }, [currentEnd]);
  
  // Video playback state
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTrimModal, setShowTrimModal] = useState(false);

  // Fetch video details on mount
  useEffect(() => {
    if (video_id) {
      dispatch(fetchVideoDetails(video_id));
    }
  }, [video_id, dispatch]);

  // Update video duration when loaded
  useEffect(() => {
    if (videoRef.current && videoDetails) {
      const video = videoRef.current;
      
      const updateTime = () => setCurrentTime(video.currentTime);
      const updateDuration = () => {
        setDuration(video.duration);
        setCurrentEnd(video.duration);
      };
      
      video.addEventListener('timeupdate', updateTime);
      video.addEventListener('loadedmetadata', updateDuration);
      
      return () => {
        video.removeEventListener('timeupdate', updateTime);
        video.removeEventListener('loadedmetadata', updateDuration);
      };
    }
  }, [videoDetails]);

  // Format time helper
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '00:00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle video controls
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const saveCurrentSegment = () => {
    const start = parseFloat(currentStart);
    const end = parseFloat(currentEnd);
    
    if (isNaN(start) || isNaN(end)) {
      message.error('Please enter valid numeric start and end times');
      return;
    }
    
    if (start >= end) {
      message.error('Start time must be less than end time');
      return;
    }
    
    if (end > duration) {
      message.error('End time cannot exceed video duration');
      return;
    }
    
    // Check for overlaps with existing segments
    for (const segment of segments) {
      const segStart = parseFloat(segment.startTime);
      const segEnd = parseFloat(segment.endTime);
      
      if ((start >= segStart && start < segEnd) || (end > segStart && end <= segEnd)) {
        message.error('This segment overlaps with an existing segment');
        return;
      }
    }
    
    setSegments(prev => [...prev, {
      id: Date.now(),
      startTime: start,
      endTime: end,
      name: currentSegmentName || `Segment ${segments.length + 1}`
    }]);
    
    // Reset values for next segment
    setCurrentSegmentName(`Segment ${segments.length + 2}`);
  };

  const removeSegment = (id) => {
    setSegments(prev => prev.filter(segment => segment.id !== id));
  };

  const editSegment = (segment) => {
    setCurrentStart(segment.startTime);
    setCurrentEnd(segment.endTime);
    setCurrentSegmentName(segment.name);
    setSegments(prev => prev.filter(s => s.id !== segment.id));
  };

  const clearAllSegments = () => {
    setSegments([]);
  };

  // Calculate total duration of all segments
  const calculateTotalDuration = () => {
    return segments.reduce((total, segment) => {
      const start = parseFloat(segment.startTime) || 0;
      const end = parseFloat(segment.endTime) || 0;
      return total + Math.max(0, end - start);
    }, 0);
  };

  // Handle trim multiple segments
  const handleTrimVideo = async () => {
    if (segments.length === 0) {
      message.error('Please add at least one segment to delete');
      return;
    }

    try {
      // Invert segments: we keep everything EXCEPT the selected segments to remove
      const sorted = [...segments].sort((a, b) => parseFloat(a.startTime) - parseFloat(b.startTime));
      const segmentsToKeep = [];
      let lastEnd = 0;
      
      for (const segment of sorted) {
        const start = parseFloat(segment.startTime);
        const end = parseFloat(segment.endTime);
        
        if (start > lastEnd) {
          segmentsToKeep.push({
            startTime: lastEnd,
            endTime: start,
            segmentName: `Keep Part`
          });
        }
        lastEnd = Math.max(lastEnd, end);
      }
      
      if (lastEnd < duration) {
        segmentsToKeep.push({
          startTime: lastEnd,
          endTime: duration,
          segmentName: `Keep Part`
        });
      }

      if (segmentsToKeep.length === 0) {
        message.error('No segments to keep after removal. The entire video would be deleted.');
        return;
      }

      // Close modal
      setShowTrimModal(false);
      
      const result = await dispatch(trimVideo({
        videoId: video_id,
        segments: segmentsToKeep,
        merge: true
      })).unwrap();
      
      message.success(result.message || 'Video trimmed successfully!');
      clearAllSegments();
      
      // Refresh video details and trigger onBack to return to video list
      await dispatch(fetchVideoDetails(video_id));
      if (onBack) onBack();
    } catch (err) {
      console.error('Trim error:', err);
      message.error(err?.toString() || 'Failed to trim video.');
    }
  };

  // Calculate preview URL
  const getPreviewUrl = () => {
    return getVideoUrl(videoDetails);
  };

  // Visual timeline rendering
  const renderTimeline = () => {
    if (!duration) return null;
    
    return (
      <div className="timeline-container mt-3" style={{ background: 'transparent', border: '1px solid var(--border-color)', padding: 16, borderRadius: 8 }}>
        <div className="timeline-header d-flex justify-content-between mb-2" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ color: 'var(--text-secondary)' }}>0:00</span>
          <span style={{ color: 'var(--text-secondary)' }}>{formatTime(duration)}</span>
        </div>
        <div className="timeline-track bg-secondary" style={{ height: '30px', position: 'relative', borderRadius: '5px', background: '#334155' }}>
          {/* Current position indicator */}
          <div 
            className="current-position-indicator"
            style={{
              position: 'absolute',
              left: `${(currentTime / duration) * 100}%`,
              height: '100%',
              width: '2px',
              backgroundColor: '#fff',
              zIndex: 10
            }}
          >
            <div className="position-label" style={{ position: 'absolute', top: '-25px', left: '-10px', background: '#0284c7', color: 'var(--text-color)', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>
              {formatTime(currentTime)}
            </div>
          </div>
          
          {/* Render segments (parts to keep) */}
          {segments.map((segment, index) => {
            const start = parseFloat(segment.startTime) || 0;
            const end = parseFloat(segment.endTime) || 0;
            const left = (start / duration) * 100;
            const width = ((end - start) / duration) * 100;
            
            return (
              <div
                key={segment.id}
                className="segment-marker"
                style={{
                  position: 'absolute',
                  left: `${left}%`,
                  width: `${width}%`,
                  height: '100%',
                  backgroundColor: '#ef4444',
                  borderRadius: '4px',
                  border: '2px solid white',
                  cursor: 'pointer'
                }}
                onClick={() => editSegment(segment)}
                title={`${segment.name}: ${formatTime(start)} - ${formatTime(end)} (To Delete)`}
              >
                <div className="segment-label" style={{
                  position: 'absolute',
                  top: '-25px',
                  fontSize: '10px',
                  color: 'var(--text-color)',
                  whiteSpace: 'nowrap'
                }}>
                  {segment.name}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-color)' }}>
        <SyncOutlined spin style={{ fontSize: 32, marginBottom: 16, color: '#3b82f6' }} />
        <div>Loading video details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert message="Error" description={error} type="error" showIcon style={{ marginBottom: 16 }} />
        <Button onClick={onBack}>Go Back</Button>
      </div>
    );
  }

  if (!videoDetails) {
    return (
      <div style={{ padding: 24 }}>
        <Alert message="Video not found" type="warning" showIcon style={{ marginBottom: 16 }} />
        <Button onClick={onBack}>Go Back</Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: 'var(--panel-color)', minHeight: '80vh', color: 'var(--text-color)', border: '1px solid var(--border-color)', borderRadius: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ color: 'var(--text-color)', margin: 0 }}>{videoDetails.video_title}</h3>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Back to Videos
        </Button>
      </div>

      <Row gutter={[24, 24]}>
        {/* Video Preview */}
        <Col xs={24} lg={16}>
          <Card 
            title={<span style={{ color: 'var(--text-color)' }}>Video Preview & Timeline</span>}
            style={{ border: '1px solid var(--border-color)', color: 'var(--text-color)' }}
            headStyle={{ borderBottom: '1px solid var(--border-color)' }}
          >
            <div 
              className="video-container" 
              style={{ position: 'relative', background: '#000', borderRadius: 8, overflow: 'hidden' }}
              onContextMenu={(e) => e.preventDefault()}
            >
              <video
                ref={videoRef}
                src={getPreviewUrl()}
                preload="auto"
                style={{ width: '100%', display: 'block' }}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                controls={false}
                controlsList="nodownload"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
            
            {/* Video Controls */}
            <div className="video-controls mt-3" style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                <span style={{ color: 'var(--text-secondary)' }}>{formatTime(currentTime)} / {formatTime(duration)}</span>
                <Slider
                  min={0}
                  max={duration || 100}
                  step={0.1}
                  value={currentTime}
                  onChange={handleSeek}
                  style={{ flex: 1 }}
                  tooltip={{ formatter: formatTime }}
                />
                <Button
                  type="primary"
                  icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                  onClick={handlePlayPause}
                />
              </div>
              
              <div style={{ display: 'flex', gap: 12 }}>
                <Button
                  type="dashed"
                  onClick={() => setCurrentStart(currentTime)}
                  style={{ flex: 1, color: '#10b981', borderColor: '#10b981' }}
                >
                  Set as Start
                </Button>
                <Button
                  type="dashed"
                  danger
                  onClick={() => setCurrentEnd(currentTime)}
                  style={{ flex: 1 }}
                >
                  Set as End
                </Button>
                <Button
                  onClick={() => handleSeek(0)}
                  style={{ flex: 1 }}
                >
                  Reset Playhead
                </Button>
              </div>
            </div>
            
            {/* Timeline Visualization */}
            {renderTimeline()}
          </Card>
        </Col>

        {/* Editing Controls */}
        <Col xs={24} lg={8}>
          <Card
            title={<span style={{ color: 'var(--text-color)' }}>Trim Segments <Tag color="blue">{segments.length}</Tag></span>}
            extra={
              <Button type="text" danger onClick={clearAllSegments} disabled={segments.length === 0}>
                Clear All
              </Button>
            }
            style={{ border: '1px solid var(--border-color)', color: 'var(--text-color)' }}
            headStyle={{ borderBottom: '1px solid var(--border-color)' }}
          >
            <Form layout="vertical">
              <Form.Item label={<span style={{ color: 'var(--text-color)' }}>Segment Name</span>}>
                <Input
                  value={currentSegmentName}
                  onChange={(e) => setCurrentSegmentName(e.target.value)}
                  placeholder="Enter segment name"
                />
              </Form.Item>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label={<span style={{ color: 'var(--text-color)' }}>Start Time</span>}>
                    <Input
                      value={startInputVal}
                      onChange={(e) => {
                        const val = e.target.value;
                        setStartInputVal(val);
                        const secs = timeToSeconds(val);
                        if (!isNaN(secs) && secs >= 0 && secs <= duration) {
                          setCurrentStart(secs);
                        }
                      }}
                      onBlur={() => {
                        setStartInputVal(secondsToTimeStr(currentStart));
                      }}
                      placeholder="00:00:00"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={<span style={{ color: 'var(--text-color)' }}>End Time</span>}>
                    <Input
                      value={endInputVal}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEndInputVal(val);
                        const secs = timeToSeconds(val);
                        if (!isNaN(secs) && secs >= 0 && secs <= duration) {
                          setCurrentEnd(secs);
                        }
                      }}
                      onBlur={() => {
                        setEndInputVal(secondsToTimeStr(currentEnd));
                      }}
                      placeholder="00:00:00"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div style={{ background: 'var(--panel-color, #ffffff)', border: '1px solid var(--border-color)', padding: 12, borderRadius: 8, marginBottom: 16 }}>
                <div>Selected Range:</div>
                <div style={{ marginTop: 4 }}>
                  <Tag color="success">{formatTime(currentStart)}</Tag>
                  {' → '}
                  <Tag color="error">{formatTime(currentEnd)}</Tag>
                </div>
                <div style={{ marginTop: 4 }}>
                  Duration: <Tag color="processing">{formatTime(Math.max(0, currentEnd - currentStart))}</Tag>
                </div>
              </div>

              <Button
                type="primary"
                onClick={saveCurrentSegment}
                icon={<PlusOutlined />}
                style={{ width: '100%', marginBottom: 20 }}
              >
                Add Segment to Delete
              </Button>
            </Form>

            {/* Segments List */}
            {segments.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Segments to Delete:</div>
                <List
                  dataSource={segments}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button size="small" icon={<EditOutlined />} onClick={() => editSegment(item)} />,
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeSegment(item.id)} />
                      ]}
                      style={{ borderBottom: '1px solid var(--border-color)' }}
                    >
                      <List.Item.Meta
                        title={<span style={{ color: 'var(--text-color)' }}>{item.name}</span>}
                        description={
                          <span style={{ color: 'var(--text-secondary)' }}>
                            {formatTime(item.startTime)} - {formatTime(item.endTime)}
                            {` (${formatTime(item.endTime - item.startTime)})`}
                          </span>
                        }
                      />
                    </List.Item>
                  )}
                  style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 20 }}
                />

                <div style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)', padding: 12, borderRadius: 8, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Segments to delete:</span>
                    <strong>{segments.length}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <span>Total duration to delete:</span>
                    <strong>{formatTime(calculateTotalDuration())}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <span>Estimated new duration:</span>
                    <strong>{formatTime(Math.max(0, duration - calculateTotalDuration()))}</strong>
                  </div>
                </div>

                <Button
                  type="primary"
                  danger
                  icon={<ScissorOutlined />}
                  onClick={() => setShowTrimModal(true)}
                  loading={triming}
                  style={{ width: '100%' }}
                >
                  Trim Video
                </Button>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Confirmation Modal */}
      <Modal
        title="Confirm Trim Video"
        open={showTrimModal}
        onCancel={() => setShowTrimModal(false)}
        onOk={handleTrimVideo}
        confirmLoading={triming}
        okText="Trim Video"
        okButtonProps={{ danger: true }}
      >
        <div style={{ textAlign: 'center', margin: '20px 0' }}>
          <ScissorOutlined style={{ fontSize: 44, color: '#ff4d4f', marginBottom: 16 }} />
          <h3>Are you sure you want to trim this video?</h3>
          <p>The selected segments will be deleted from the video.</p>
        </div>
        <div style={{ background: 'rgba(0,0,0,0.02)', padding: 16, borderRadius: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Segments to Delete:</span>
            <strong>{segments.length}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <span>Total Duration to Delete:</span>
            <strong>{formatTime(calculateTotalDuration())}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <span>Estimated New Duration:</span>
            <strong>{formatTime(Math.max(0, duration - calculateTotalDuration()))}</strong>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminEditVideo;
